import {
  AttendanceCode,
  AttendanceEntriesCounts,
  DEFAULT_DATE_FORMAT_FNS,
  JustificationCounts,
  SchoolYear,
  useGetAttendanceCodesQuery,
  useGetEnrollments,
  useGetRelationAttendanceAggregateQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { format } from 'date-fns';
import { useMemo } from 'react';

import { toWeekRows } from '../common/attendanceGridData';
import { buildStudentGridFromAggregate } from '../common/buildStudentGrid';
import { getRegistrationValidityIntervals } from '../common/studentValidity';
import { AttendanceGridColumn, AttendanceGridStudent, AttendanceWeekRow } from '../common/types';

type UseAttendanceGridForStudentResult = {
  columns: AttendanceGridColumn[];
  rows: AttendanceWeekRow[];
  codes: AttendanceCode[];
  counts: AttendanceEntriesCounts[];
  justificationCounts?: JustificationCounts;
  isLoading: boolean;
};

export const useAttendanceGridForStudent = (
  student: AttendanceGridStudent,
  selectedYear: SchoolYear | undefined,
): UseAttendanceGridForStudentResult => {
  const relationId = student.relation_id;
  const { schoolId = '' } = useAuth();

  const today = format(new Date(), DEFAULT_DATE_FORMAT_FNS);

  const { data: enrollmentsData, isLoading: enrollmentsLoading } = useGetEnrollments(
    { schoolId, studentId: relationId },
    { enabled: !!schoolId && !!relationId, refetchOnMount: 'always' },
  );

  const { data: codes = [] } = useGetAttendanceCodesQuery(schoolId, { enabled: !!schoolId });

  const dateTo = selectedYear && selectedYear.end < today ? selectedYear.end : today;

  const enabled = Boolean(relationId && schoolId && selectedYear);

  const { data: aggregate, isLoading: aggregateLoading } = useGetRelationAttendanceAggregateQuery(
    {
      relationId,
      school_id: schoolId,
      date_from: selectedYear?.start ?? '',
      date_to: dateTo,
    },
    { enabled, refetchOnMount: 'always' },
  );

  const validity = useMemo(
    () =>
      selectedYear
        ? getRegistrationValidityIntervals(enrollmentsData?.enrollments ?? [], selectedYear.id)
        : [],
    [enrollmentsData, selectedYear],
  );

  const { counts, justificationCounts } = useMemo(() => {
    if (!aggregate) {
      return { counts: [], justificationCounts: undefined };
    }

    return {
      counts: aggregate.total.counts.map((count, order) => ({
        attendance_code_id: count.attendance_code_id,
        count: count.count,
        order,
      })),
      justificationCounts: aggregate.total.justification_counts,
    };
  }, [aggregate]);

  const { columns, rows } = useMemo(() => {
    if (!aggregate) return { columns: [], rows: [] };

    const grid = buildStudentGridFromAggregate({
      aggregate,
      validity,
      student,
      today: new Date(),
    });
    return { columns: grid.columns, rows: toWeekRows(grid.rows) };
  }, [aggregate, validity, student]);

  return {
    columns,
    rows,
    codes,
    counts,
    justificationCounts,
    isLoading: enrollmentsLoading || aggregateLoading,
  };
};
